import useQueryHelper from '@/hooks/use-query-helper';
import { Box, Theme } from '@mui/material';
import MuiCard from '@mui/material/Card';
import classNames from 'classnames';
import { FC, ReactNode, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { UseQueryResult } from 'react-query';
import Loader from '../loader';

interface Props {
  children: ReactNode;
  title?: ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
  size?: 'small' | 'medium';
  query?: UseQueryResult;
}

const useStyles = createUseStyles<string, { size: 'medium' | 'small' }>((theme: Theme) => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    '&.clickable': {
      cursor: 'pointer',
    },
    display: 'flex',
    flexDirection: 'column',
    minWidth: 80,
  },
  inner: ({ size }) => ({
    padding: size === 'small' ? 8 : 20,
    color: theme.palette.darkGray,
    flexGrow: 1,
    width: '100%',
    boxSizing: 'border-box',
  }),
  header: {
    padding: '16px 20px 0',
    color: theme.palette.secondary.main,
  },
}));

const Card: FC<Props> = ({
  children,
  className = '',
  onClick,
  hover = false,
  size = 'medium',
  title = '',
  query,
}) => {
  const styles = useStyles({ size });
  const compClass = classNames(styles.card, className, { clickable: !!onClick });
  const [elevation, setElevation] = useState(0);
  const { showLoader } = useQueryHelper(query);

  const handleClick = () => {
    onClick?.();
  };

  const handleHover = (enabled: boolean) => {
    if (!hover) {
      return;
    }
    setElevation(enabled ? 1 : 0);
  };

  const hasTitle = !!title;

  return (
    <MuiCard
      elevation={elevation}
      className={compClass}
      onClick={handleClick}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      {showLoader ? (
        <Box className={styles.inner}>
          <Loader />
        </Box>
      ) : (
        <>
          {hasTitle && <Box className={styles.header}>{title}</Box>}
          <Box className={styles.inner}>{children}</Box>
        </>
      )}
    </MuiCard>
  );
};

export default Card;
